import React, { useState, useMemo } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Tooltip, Legend, Filler } from 'chart.js';
import { useApp } from '../../context/AppContext';
import { formatCurrency, shortMonths } from '../../utils/formatters';
import { exportReportPDF, exportCSV } from '../../utils/pdfExport';
import { toast } from '../shared/Toast';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Tooltip, Legend, Filler);

const tabs = [
  { id: 'cashflow', label: 'Fluxo de caixa', icon: 'fa-money-bill-trend-up' },
  { id: 'category', label: 'Por categoria', icon: 'fa-chart-pie' },
  { id: 'evolution', label: 'Evolução', icon: 'fa-chart-line' },
];

export default function Reports() {
  const { state } = useApp();
  const [activeTab, setActiveTab] = useState('cashflow');
  const [period, setPeriod] = useState('6');

  const cats = useMemo(() => Object.fromEntries(state.categories.map(c => [c.id, c])), [state.categories]);
  const now = new Date();

  const months = useMemo(() => {
    const n = parseInt(period);
    return Array.from({ length: n }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (n - 1 - i), 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
  }, [period]);

  const monthLabels = months.map(m => shortMonths[parseInt(m.split('-')[1]) - 1] + '/' + m.split('-')[0].slice(2));

  const flowData = useMemo(() =>
    months.map(m => {
      const txs = state.transactions.filter(t => t.date.startsWith(m));
      return {
        income: txs.filter(t => t.type === 'income' && t.status === 'paid' && !t.cardId).reduce((s, t) => s + t.value, 0),
        expense: txs.filter(t => t.type === 'expense' && t.status === 'paid' && !t.cardId).reduce((s, t) => s + t.value, 0),
      };
    }), [state.transactions, months]);

  const totalIncome = flowData.reduce((s, d) => s + d.income, 0);
  const totalExpense = flowData.reduce((s, d) => s + d.expense, 0);
  const savings = totalIncome - totalExpense;

  const catExpenses = useMemo(() => {
    const map = {};
    state.transactions.filter(t => months.some(m => t.date.startsWith(m)) && t.type === 'expense' && t.status === 'paid')
      .forEach(t => { map[t.categoryId] = (map[t.categoryId] || 0) + t.value; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [state.transactions, months]);

  const catColors = ['#0d1b2a','#c9a227','#378ADD','#1d9e75','#e24b4a','#993556','#854F0B','#185FA5','#3B6D11','#888780'];

  const patrimonyData = useMemo(() => {
    // Walk backwards: current balance is end of last month; subtract each month's net to get prior balance
    let balance = state.accounts.reduce((s, a) => s + a.balance, 0);
    const result = new Array(months.length);
    result[months.length - 1] = balance;
    for (let i = months.length - 2; i >= 0; i--) {
      balance -= (flowData[i + 1].income - flowData[i + 1].expense);
      result[i] = balance;
    }
    return result;
  }, [flowData, months, state.accounts]);

  const periodTxs = useMemo(() =>
    state.transactions.filter(t => months.some(m => t.date.startsWith(m))).sort((a, b) => new Date(b.date) - new Date(a.date)),
    [state.transactions, months]
  );
  const topExpenses = useMemo(() =>
    [...periodTxs].filter(t => t.type === 'expense' && t.status === 'paid').sort((a, b) => b.value - a.value).slice(0, 5),
    [periodTxs]
  );

  const periodLabel = period === '1' ? 'Este mês' : `Últimos ${period} meses`;

  const handlePDF = () => {
    exportReportPDF({ title: 'Relatório Financeiro', period: periodLabel, totalIncome, totalExpense, transactions: periodTxs, categories: state.categories });
    toast.info('Abrindo relatório PDF...');
  };
  const handleCSV = () => { exportCSV(periodTxs, state.categories); toast.success('CSV exportado!'); };

  const chartOptions = (yCallback) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + formatCurrency(ctx.parsed.y ?? ctx.parsed) } } },
    scales: { x: { grid: { display: false }, ticks: { font: { size: 10 } } }, y: { grid: { color: 'rgba(128,128,128,0.1)' }, ticks: { font: { size: 10 }, callback: yCallback } } }
  });
  const fmt1k = v => 'R$' + Math.round(v / 1000) + 'k';

  return (
    <div className="page-enter">
      <div className="row" style={{ marginBottom: 14 }}>
        <span style={{ fontSize: 17, fontWeight: 600 }}>Relatórios</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={handleCSV}><i className="fas fa-file-csv"></i> CSV</button>
          <button className="btn btn-primary btn-sm" onClick={handlePDF}><i className="fas fa-file-pdf"></i> PDF</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {['1','3','6','12'].map(p => (
          <button key={p} className={`tag ${period === p ? 'tag-blue' : 'tag-gray'}`} style={{ cursor: 'pointer', border: 'none' }} onClick={() => setPeriod(p)}>
            {p === '1' ? 'Este mês' : `${p} meses`}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.id} className={`tag ${activeTab === t.id ? 'tag-blue' : 'tag-gray'}`} style={{ cursor: 'pointer', border: 'none', whiteSpace: 'nowrap', padding: '5px 12px' }} onClick={() => setActiveTab(t.id)}>
            <i className={`fas ${t.icon}`}></i> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'cashflow' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div className="card-sm" style={{ textAlign: 'center' }}>
              <div className="metric-label">Receitas</div>
              <div className="metric-value text-green" style={{ fontSize: 15 }}>{formatCurrency(totalIncome)}</div>
            </div>
            <div className="card-sm" style={{ textAlign: 'center' }}>
              <div className="metric-label">Despesas</div>
              <div className="metric-value text-red" style={{ fontSize: 15 }}>{formatCurrency(totalExpense)}</div>
            </div>
            <div className="card-sm" style={{ textAlign: 'center' }}>
              <div className="metric-label">Saldo</div>
              <div className="metric-value" style={{ fontSize: 15, color: savings >= 0 ? 'var(--green)' : 'var(--red)' }}>{formatCurrency(savings)}</div>
            </div>
          </div>

          <div className="card">
            <div className="section-title">Receitas vs despesas</div>
            <div style={{ height: 200 }}>
              <Bar data={{ labels: monthLabels, datasets: [
                { label: 'Receitas', data: flowData.map(d => d.income), backgroundColor: '#1d9e75', borderRadius: 5 },
                { label: 'Despesas', data: flowData.map(d => d.expense), backgroundColor: '#e24b4a', borderRadius: 5 },
              ]}} options={chartOptions(fmt1k)} />
            </div>
            <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 11, color: 'var(--muted)' }}>
              <span><span style={{ width: 10, height: 10, borderRadius: 2, background: '#1d9e75', display: 'inline-block', marginRight: 4 }}></span>Receitas</span>
              <span><span style={{ width: 10, height: 10, borderRadius: 2, background: '#e24b4a', display: 'inline-block', marginRight: 4 }}></span>Despesas</span>
            </div>
          </div>

          <div className="card">
            <div className="section-title">Saldo mensal (diferença)</div>
            <div style={{ height: 150 }}>
              <Bar data={{ labels: monthLabels, datasets: [{
                label: 'Saldo', data: flowData.map(d => d.income - d.expense),
                backgroundColor: flowData.map(d => d.income - d.expense >= 0 ? 'rgba(29,158,117,0.8)' : 'rgba(226,75,74,0.8)'),
                borderRadius: 5,
              }]}} options={{ ...chartOptions(fmt1k), plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + formatCurrency(ctx.parsed.y) } } } }} />
            </div>
          </div>

          {topExpenses.length > 0 && (
            <div className="card">
              <div className="section-title">Maiores gastos do período</div>
              {topExpenses.map((tx, i) => (
                <div className="tx-item" key={tx.id}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--muted)', flexShrink: 0 }}>{i + 1}</div>
                  <div className="tx-info"><div className="tx-desc">{tx.description}</div><div className="tx-cat">{cats[tx.categoryId]?.name}</div></div>
                  <div className="tx-right"><div className="tx-amount text-red">-{formatCurrency(tx.value)}</div></div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'category' && (
        <>
          {catExpenses.length === 0 ? (
            <div className="empty-state card"><i className="fas fa-chart-pie"></i><div>Sem dados de despesa no período</div></div>
          ) : (
            <>
              <div className="card">
                <div className="section-title">Distribuição por categoria</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ width: 140, height: 140, flexShrink: 0 }}>
                    <Doughnut data={{ labels: catExpenses.map(([id]) => cats[id]?.name || id), datasets: [{ data: catExpenses.map(([, v]) => v), backgroundColor: catColors, borderWidth: 2, borderColor: 'var(--surface)' }] }}
                      options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + formatCurrency(ctx.parsed) } } }, cutout: '60%' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    {catExpenses.slice(0, 6).map(([id, val], i) => {
                      const total = catExpenses.reduce((s, [, v]) => s + v, 0);
                      return (
                        <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, fontSize: 12 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: catColors[i % catColors.length], display: 'inline-block', flexShrink: 0 }}></span>
                          <span style={{ flex: 1 }}>{cats[id]?.name || id}</span>
                          <span style={{ color: 'var(--muted)', fontWeight: 500 }}>{Math.round(val / total * 100)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="section-title">Gastos por categoria</div>
                <div style={{ height: Math.max(200, catExpenses.length * 38 + 40) }}>
                  <Bar data={{ labels: catExpenses.map(([id]) => cats[id]?.name || id), datasets: [{ label: 'Gasto', data: catExpenses.map(([, v]) => v), backgroundColor: catColors, borderRadius: 4 }] }}
                    options={{ indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + formatCurrency(ctx.parsed.x) } } }, scales: { x: { grid: { color: 'rgba(128,128,128,0.1)' }, ticks: { font: { size: 10 }, callback: fmt1k } }, y: { grid: { display: false }, ticks: { font: { size: 11 } } } } }} />
                </div>
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'evolution' && (
        <>
          <div className="card-sm" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="metric-label">Patrimônio atual</div>
                <div className="metric-value text-gold count-anim">{formatCurrency(state.accounts.reduce((s, a) => s + a.balance, 0))}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="metric-label">Variação no período</div>
                <div style={{ fontWeight: 600, fontSize: 16, color: savings >= 0 ? 'var(--green)' : 'var(--red)' }}>{savings >= 0 ? '+' : ''}{formatCurrency(savings)}</div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="section-title">Evolução patrimonial</div>
            <div style={{ height: 200 }}>
              <Line data={{ labels: monthLabels, datasets: [{ label: 'Patrimônio', data: patrimonyData, borderColor: '#c9a227', backgroundColor: 'rgba(201,162,39,0.1)', fill: true, tension: 0.4, pointBackgroundColor: '#c9a227', pointRadius: 4, pointHoverRadius: 6 }] }}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + formatCurrency(ctx.parsed.y) } } }, scales: { x: { grid: { display: false }, ticks: { font: { size: 10 } } }, y: { grid: { color: 'rgba(128,128,128,0.1)' }, ticks: { font: { size: 10 }, callback: fmt1k } } } }} />
            </div>
          </div>

          <div className="card">
            <div className="section-title">Comparativo mensal</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Mês','Receitas','Despesas','Saldo'].map(h => <th key={h} style={{ textAlign: h === 'Mês' ? 'left' : 'right', padding: '7px 6px', color: 'var(--muted)', fontWeight: 500 }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {months.map((m, i) => {
                    const d = flowData[i]; const s = d.income - d.expense;
                    return (
                      <tr key={m} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 6px', fontWeight: 500 }}>{monthLabels[i]}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--green)' }}>+{formatCurrency(d.income)}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--red)' }}>-{formatCurrency(d.expense)}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'right', color: s >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>{formatCurrency(s)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
